import {
  ForgotPasswordConfirm,
  ForgotPasswordRequest,
  Loader,
  NewPasswordLogin,
  TotpCodeLogin,
  TotpSetupLogin,
  UsernamePasswordLogin,
} from "@/shared/coralreef";
import { useTranslation } from "react-i18next";
import type { SignInResult } from "../../shell-handle/auth";
import type { QuickLoginSuccess } from "../../shell-handle/quickLogin";
import {
  QuickLoginSteps,
  useQuickLoginFlow,
  type QuickLoginController,
} from "./QuickLogin";
import { SIGN_IN_FAILED, useErrorToast } from "./useErrorToast";
import { useLoginSteps } from "./useLoginSteps";

const NEW_PASSWORD_REQUIRED = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";
const TOTP_CODE_REQUIRED = "CONFIRM_SIGN_IN_WITH_TOTP_CODE";
const TOTP_SETUP_REQUIRED = "CONTINUE_SIGN_IN_WITH_TOTP_SETUP";
const RESET_PASSWORD_REQUIRED = "RESET_PASSWORD";
const CONFIRM_RESET_WITH_CODE = "CONFIRM_RESET_PASSWORD_WITH_CODE";

export interface LoginPageProps {
  signIn(email: string, password: string): Promise<SignInResult>;
  confirmSignIn(challengeResponse: string): Promise<SignInResult>;
  isTotpEnrolmentRequired(): Promise<boolean>;
  startTotpSetup(): Promise<string>;
  completeTotpSetup(code: string): Promise<void>;
  abandonSignIn(): Promise<void>;
  resetPassword(
    username: string,
  ): Promise<{ nextStep?: { resetPasswordStep: string } }>;
  confirmResetPassword(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void>;
  onSuccess(): void | Promise<void>;
  quickLogin?: QuickLoginController | null;
  onQuickLoginSuccess(outcome: QuickLoginSuccess): void | Promise<void>;
}

export function LoginPage({
  signIn,
  confirmSignIn,
  isTotpEnrolmentRequired,
  startTotpSetup,
  completeTotpSetup,
  abandonSignIn,
  resetPassword,
  confirmResetPassword,
  onSuccess,
  quickLogin = null,
  onQuickLoginSuccess,
}: LoginPageProps) {
  const { t } = useTranslation();
  const { toastOnFailure, errorMessage } = useErrorToast();
  const hasAccounts = !!quickLogin?.rows.length;
  const {
    step,
    error,
    info,
    loginEmail,
    resetUsername,
    totpSetup,
    submitting,
    changingPin,
    actions,
  } = useLoginSteps(hasAccounts ? "accounts" : "login");
  const { goTo, openLoginForm } = actions;

  const quick = useQuickLoginFlow({
    quickLogin,
    onQuickLoginSuccess,
    onSuccess,
    onStep: goTo,
    onLoginForm: openLoginForm,
    changingPin,
  });

  const submit = (work: () => Promise<unknown>) => {
    if (submitting) return;
    actions.submitting();
    return work().finally(actions.settled);
  };

  const submitOrToast = (work: () => Promise<unknown>, fallback: string) => {
    const running = submit(work);
    if (running) toastOnFailure(running, fallback);
  };

  const abandon = async (e: unknown): Promise<never> => {
    await abandonSignIn();
    goTo("login");
    throw e;
  };

  const finishSignIn = async () => {
    let secret: string | null = null;
    try {
      if (await isTotpEnrolmentRequired()) secret = await startTotpSetup();
    } catch (e) {
      return abandon(e);
    }
    if (secret) {
      actions.totpRequired({ secret, via: "enrolment" });
      return;
    }
    await completeLogin();
  };

  const completeLogin = async () => {
    if (await quick.offerEnrolment()) return;
    await onSuccess();
  };

  const handleSignInResult = async (r: SignInResult) => {
    if (r.isSignedIn) return finishSignIn();
    const signInStep = r.nextStep?.signInStep;
    if (signInStep === NEW_PASSWORD_REQUIRED) {
      goTo("new-password");
    } else if (signInStep === TOTP_CODE_REQUIRED) {
      goTo("totp-code");
    } else if (signInStep === TOTP_SETUP_REQUIRED && r.nextStep?.totpSecret) {
      actions.totpRequired({ secret: r.nextStep.totpSecret, via: "challenge" });
    } else if (signInStep === RESET_PASSWORD_REQUIRED) {
      actions.resetRequired();
    } else {
      return abandon(new Error(`Unsupported sign-in step: ${signInStep}`));
    }
  };

  const backToLogin = () => {
    goTo("cancelling");
    toastOnFailure(
      abandonSignIn().finally(actions.totpLeft),
      "Failed to cancel sign-in",
    );
  };

  const onLoginPasswordSubmitHandler = (username: string, password: string) =>
    submitOrToast(
      () => signIn(username, password).then(handleSignInResult),
      SIGN_IN_FAILED,
    );

  const onNewPasswordSubmitHandler = (newPassword: string) =>
    submitOrToast(
      () => confirmSignIn(newPassword).then(handleSignInResult),
      "Failed to set new password",
    );

  const onTotpCodeSubmitHandler = (code: string) =>
    submitOrToast(
      () => confirmSignIn(code).then(handleSignInResult),
      "Failed to verify code",
    );

  const onTotpSetupSubmitHandler = (code: string) =>
    submitOrToast(
      () =>
        totpSetup?.via === "enrolment"
          ? completeTotpSetup(code).then(() => completeLogin())
          : confirmSignIn(code).then(handleSignInResult),
      "Failed to set up authenticator",
    );

  const onForgotRequestSubmitHandler = (username: string) => {
    actions.fail(null);
    void submit(() =>
      resetPassword(username)
        .then((r) => {
          if (r.nextStep?.resetPasswordStep === CONFIRM_RESET_WITH_CODE) {
            actions.resetCodeSent(username);
          } else {
            actions.passwordReset();
          }
        })
        .catch((e) => {
          actions.fail(errorMessage(e, "Failed to start password reset"));
        }),
    );
  };

  const onForgotConfirmSubmitHandler = (
    confirmationCode: string,
    newPassword: string,
  ) => {
    actions.fail(null);
    void submit(() =>
      confirmResetPassword(resetUsername, confirmationCode, newPassword)
        .then(actions.passwordReset)
        .catch((e) => {
          actions.fail(errorMessage(e, "Failed to reset password"));
        }),
    );
  };

  return (
    <div className="flex h-[75vh] w-screen items-center justify-center">
      {submitting && <Loader />}
      <div
        className={`${submitting ? "hidden" : "flex"} flex-col items-center gap-3`}
      >
        <QuickLoginSteps
          step={step}
          rows={quickLogin?.rows ?? []}
          online={quickLogin?.online ?? true}
          flow={quick}
          goTo={goTo}
          onChangePin={actions.changePin}
          onLoginForm={() => openLoginForm()}
          onPasswordSubmit={onLoginPasswordSubmitHandler}
        />
        {step === "login" && (
          <UsernamePasswordLogin
            key={loginEmail ?? ""}
            initialUsername={loginEmail}
            onSubmit={onLoginPasswordSubmitHandler}
            onForgotPassword={() => goTo("forgot-request")}
          />
        )}
        {step === "new-password" && (
          <NewPasswordLogin onSubmit={onNewPasswordSubmitHandler} />
        )}
        {step === "totp-code" && (
          <TotpCodeLogin
            onSubmit={onTotpCodeSubmitHandler}
            onBackToLogin={backToLogin}
          />
        )}
        {step === "totp-setup" && totpSetup && (
          <TotpSetupLogin
            secret={totpSetup.secret}
            onSubmit={onTotpSetupSubmitHandler}
            onBackToLogin={backToLogin}
          />
        )}
        {step === "forgot-request" && (
          <ForgotPasswordRequest
            onSubmit={onForgotRequestSubmitHandler}
            onBackToLogin={() => goTo("login")}
          />
        )}
        {step === "forgot-confirm" && (
          <ForgotPasswordConfirm
            onSubmit={onForgotConfirmSubmitHandler}
            onBackToLogin={() => goTo("login")}
          />
        )}
        {info && (
          <div
            role="status"
            data-testid="info-message"
            className="text-sm w-[300px]"
          >
            {t(info)}
          </div>
        )}
        {error && (
          <div
            role="alert"
            data-testid="error-message"
            className="text-red-500 text-sm w-[300px]"
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
